import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAgents, useCalendrierAbsences, useStructures } from '@/api/hooks';
import { CATEGORIES_CONGE, LIBELLE_CATEGORIE_CONGE, type EvenementCalendrier } from '@/api/types';
import { accedeModule } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { Button } from '@/components/ui/button';
import { SelectNatif } from '@/components/ui/select-natif';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, dateFr } from '@/lib/utils';

type Vue = 'semaine' | 'mois' | 'annee';
const COULEUR: Record<EvenementCalendrier['type'], string> = { ENVISAGE: 'bg-slate-200 text-slate-800', SOUMIS: 'bg-amber-200 text-amber-900', APPROUVE: 'bg-sky-200 text-sky-900', PRIS: 'bg-emerald-300 text-emerald-950' };
const LIBELLE_TYPE: Record<EvenementCalendrier['type'], string> = { ENVISAGE: 'Congé envisagé', SOUMIS: 'Demande soumise', APPROUVE: 'Congé approuvé', PRIS: 'Congé pris' };
const iso = (d: Date) => d.toISOString().slice(0, 10);
const ajouter = (s: string, n: number) => iso(new Date(new Date(`${s}T00:00:00Z`).getTime() + n * 86_400_000));

function bornes(vue: Vue, ancre: string): { debut: string; fin: string; titre: string } {
  const d = new Date(`${ancre}T00:00:00Z`);
  if (vue === 'semaine') {
    const lundi = ajouter(ancre, -((d.getUTCDay() + 6) % 7));
    return { debut: lundi, fin: ajouter(lundi, 6), titre: `Semaine du ${dateFr(lundi)}` };
  }
  if (vue === 'mois') {
    const debut = iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
    const fin = iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
    return { debut, fin, titre: new Date(`${debut}T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' }) };
  }
  return { debut: `${d.getUTCFullYear()}-01-01`, fin: `${d.getUTCFullYear()}-12-31`, titre: `Année ${d.getUTCFullYear()}` };
}

/** Calendrier des absences (§ 4.6) : vues semaine, mois, année ; quatre états ; périmètre selon le profil ; catégories sensibles masquées. */
export function CalendrierAbsences() {
  const u = useSession()!.utilisateur;
  const [params, setParams] = useSearchParams();
  const [vue, setVue] = useState<Vue>(() => (['semaine', 'mois', 'annee'] as const).find((v) => v === params.get('vue')) ?? 'mois');
  // Ancrage par l'adresse (?date=AAAA-MM-JJ) : liens depuis les notifications et captures de la documentation.
  const [ancre, setAncre] = useState(() => (/^\d{4}-\d{2}-\d{2}$/.test(params.get('date') ?? '') ? params.get('date')! : iso(new Date())));
  const [agentId, setAgentId] = useState('');
  const [categorie, setCategorie] = useState('');
  const [etat, setEtat] = useState('');
  const structure = params.get('structure') ?? '';
  const perimetre = accedeModule(u.profils, 'calendrier');
  const structures = useStructures();
  const agents = useAgents(structure || undefined);
  const b = bornes(vue, ancre);
  const { data, isLoading, error } = useCalendrierAbsences({ debut: b.debut, fin: b.fin, structure: structure || undefined, agentId: agentId || undefined, categorie: categorie || undefined, etat: etat || undefined });
  const jours = useMemo(() => {
    const out: string[] = [];
    for (let j = b.debut; j <= b.fin; j = ajouter(j, 1)) out.push(j);
    return out;
  }, [b.debut, b.fin]);
  const lignes = useMemo(() => {
    const parAgent = new Map<string, { nom: string; structureCode: string | null; evenements: EvenementCalendrier[] }>();
    for (const e of data ?? []) {
      const l = parAgent.get(e.agent.id) ?? { nom: e.agent.nom, structureCode: e.agent.structureCode, evenements: [] };
      l.evenements.push(e);
      parAgent.set(e.agent.id, l);
    }
    return [...parAgent.entries()].sort((x, y) => x[1].nom.localeCompare(y[1].nom));
  }, [data]);
  const naviguer = (sens: -1 | 1) => {
    const d = new Date(`${ancre}T00:00:00Z`);
    if (vue === 'semaine') d.setUTCDate(d.getUTCDate() + 7 * sens);
    else if (vue === 'mois') d.setUTCMonth(d.getUTCMonth() + sens);
    else d.setUTCFullYear(d.getUTCFullYear() + sens);
    setAncre(iso(d));
  };
  return (
    <div>
      <PageHeader titre="Calendrier des absences" sousTitre={perimetre ? 'Congés envisagés, soumis, approuvés et pris des agents de votre périmètre ; une intention ne vaut pas autorisation de départ.' : 'Vos congés envisagés, soumis, approuvés et pris.'} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={vue} onValueChange={(v) => setVue(v as Vue)}>
          <TabsList>
            <TabsTrigger value="semaine">Semaine</TabsTrigger>
            <TabsTrigger value="mois">Mois</TabsTrigger>
            <TabsTrigger value="annee">Année</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" aria-label="Précédent" onClick={() => naviguer(-1)}>
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-40 text-center text-sm font-medium capitalize">{b.titre}</span>
          <Button variant="outline" size="icon" aria-label="Suivant" onClick={() => naviguer(1)}>
            <ChevronRight className="size-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAncre(iso(new Date()))}>
            Aujourd'hui
          </Button>
        </div>
        {perimetre && (
          <SelectNatif value={structure} onChange={(e) => setParams(e.target.value ? { structure: e.target.value } : {})}>
            <option value="">Toutes mes structures</option>
            {(structures.data ?? []).map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} · {s.libelle}
              </option>
            ))}
          </SelectNatif>
        )}
        {perimetre && (
          <SelectNatif value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">Tous les agents</option>
            {(agents.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.prenom} {a.nom}
              </option>
            ))}
          </SelectNatif>
        )}
        <SelectNatif value={categorie} onChange={(e) => setCategorie(e.target.value)}>
          <option value="">Toutes les catégories</option>
          {CATEGORIES_CONGE.map((c) => (
            <option key={c} value={c}>
              {LIBELLE_CATEGORIE_CONGE[c]}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={etat} onChange={(e) => setEtat(e.target.value)}>
          <option value="">Tous les états</option>
          {(Object.keys(LIBELLE_TYPE) as EvenementCalendrier['type'][]).map((t) => (
            <option key={t} value={t}>
              {LIBELLE_TYPE[t]}
            </option>
          ))}
        </SelectNatif>
      </div>
      <div className="mb-3 flex flex-wrap gap-3 text-xs" data-testid="legende">
        {(Object.keys(LIBELLE_TYPE) as EvenementCalendrier['type'][]).map((t) => (
          <span key={t} className="flex items-center gap-1">
            <span className={cn('inline-block size-3 rounded', COULEUR[t])} /> {LIBELLE_TYPE[t]}
          </span>
        ))}
      </div>
      {isLoading && !data ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : vue === 'annee' ? (
        <Annee evenements={data ?? []} annee={Number(b.debut.slice(0, 4))} />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card" data-testid="grille-absences">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 min-w-44 border-b bg-card p-2 text-left font-medium">Agent</th>
                {jours.map((j) => {
                  const d = new Date(`${j}T00:00:00Z`);
                  const we = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                  return (
                    <th key={j} className={cn('border-b border-l p-1 text-center font-normal', we && 'bg-muted/60 text-muted-foreground')} title={dateFr(j)}>
                      <div>{['D', 'L', 'M', 'M', 'J', 'V', 'S'][d.getUTCDay()]}</div>
                      <div>{d.getUTCDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {lignes.length === 0 && (
                <tr>
                  <td colSpan={jours.length + 1} className="p-4 text-center text-muted-foreground">
                    Aucune absence sur la période.
                  </td>
                </tr>
              )}
              {lignes.map(([id, l]) => (
                <tr key={id}>
                  <td className="sticky left-0 z-10 border-b bg-card p-2">
                    <div className="font-medium">{l.nom}</div>
                    <div className="text-muted-foreground">{l.structureCode}</div>
                  </td>
                  {jours.map((j) => {
                    const e = l.evenements.find((x) => x.debut <= j && j <= x.fin);
                    const d = new Date(`${j}T00:00:00Z`);
                    const we = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                    return (
                      <td key={j} className={cn('border-b border-l p-0', we && 'bg-muted/40')}>
                        {e && (
                          <Link to={e.type === 'ENVISAGE' ? '/rh' : `/rh/conges/${e.id}`} className={cn('block h-7 w-full', COULEUR[e.type])} title={`${e.libelle} · ${LIBELLE_TYPE[e.type]} · du ${dateFr(e.debut)} au ${dateFr(e.fin)}`} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Annee({ evenements, annee }: { evenements: EvenementCalendrier[]; annee: number }) {
  const mois = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="annee-absences">
      {mois.map((m) => {
        const debut = iso(new Date(Date.UTC(annee, m, 1)));
        const fin = iso(new Date(Date.UTC(annee, m + 1, 0)));
        const du = evenements.filter((e) => e.debut <= fin && e.fin >= debut);
        const compte = (t: EvenementCalendrier['type']) => new Set(du.filter((e) => e.type === t).map((e) => e.agent.id)).size;
        return (
          <div key={m} className="rounded-lg border bg-card p-3">
            <div className="font-medium capitalize">{new Date(`${debut}T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' })}</div>
            <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
              {(Object.keys(LIBELLE_TYPE) as EvenementCalendrier['type'][]).map((t) => (
                <div key={t} className="flex items-center gap-1">
                  <span className={cn('inline-block size-2.5 rounded', COULEUR[t])} /> {compte(t)} agent{compte(t) > 1 ? 's' : ''}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
